import { useEffect, useState, type FormEvent } from 'react'
import { useLocation } from 'react-router'
import { Box } from '@mui/material'
import { useIntl } from 'react-intl'

import InlineMessage from '@base/InlineMessage/InlineMessage.component'
import TextField from '@base/TextField/TextField.component'
import Button from '@base/Button/Button.component'
import LockIcon from '@icons/Lock.icon'
import AuthError from '@components/AuthError/AuthError.component'
import AuthLoading from '@components/AuthLoading/AuthLoading.component'
import SplitAuthLayout from '@components/SplitAuthLayout/SplitAuthLayout.component'
import SubmitRow from '@components/SubmitRow/SubmitRow.component'
import BackButton from '@components/BackButton'
import { defaultReturnUrl, isTrustedReturnTo } from '@utils/url.utils'
import { markRecoveryJustCompleted } from '@utils/recovery-handoff.utils'

import { useInviteState } from './hooks/useInviteState'
import { useSetInitialPassword } from './hooks/useSetInitialPassword'

// Landing page for the invitation mail. The one-time code rides in the URL,
// which is only safe because nothing here submits on its own: mail security
// scanners render the page (JavaScript included) seconds after delivery, and
// an auto-submit would hand them the code. Every request below is behind the
// user's click.
const InvitePage = () => {
  const intl = useIntl()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const flowId = params.get('flow')
  const invitedEmail = params.get('email')
  const rawReturn = params.get('return_to')
  const returnTo = rawReturn && isTrustedReturnTo(rawReturn) ? rawReturn : defaultReturnUrl()

  // Read the code once, then strip it from the address bar so it does not
  // outlive the visit in history or a session restore. Lazy init so
  // StrictMode's double render does not re-read an already-stripped URL.
  const [code] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('code'),
  )
  useEffect(() => {
    const url = new URL(window.location.href)
    if (!url.searchParams.has('code')) {
      return
    }
    url.searchParams.delete('code')
    window.history.replaceState(null, '', url.toString())
  }, [])

  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')

  const bothFilled = password !== '' && confirmation !== ''
  const mismatch = bothFilled && password !== confirmation

  const state = useInviteState(flowId, code, invitedEmail)
  const goToSettings = (): void => {
    // /settings picks its tab from this flag; without it an invitee who has no
    // password yet lands on Profile with nothing telling them what to do.
    markRecoveryJustCompleted()

    window.location.replace(`/settings?return_to=${encodeURIComponent(returnTo)}`)
  }
  const { submitting, passwordError, formError, submit, clearPasswordError } =
    useSetInitialPassword({
      flowId,
      code,
      returnTo,
      onLinkDead: () => {
        window.location.assign(recoveryHref(returnTo))
      },
      onCodeSpent: goToSettings,
    })

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    submit(password, confirmation).catch(() => undefined)
  }

  // The code was already spent and the session is provably the invitee's —
  // /settings is the canonical place to finish, no need to mirror it here.
  const shouldResume = state.kind === 'resume'
  useEffect(() => {
    if (shouldResume) {
      goToSettings()
    }
    // goToSettings is derived from returnTo alone; re-running on identity
    // changes would fire the redirect twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldResume, returnTo])

  if (state.kind === 'loading' || state.kind === 'resume') {
    return <AuthLoading />
  }

  if (state.kind === 'error') {
    return (
      <AuthError
        welcome={intl.formatMessage({ id: 'invite.error.welcome' })}
        message={state.message}
      />
    )
  }

  if (state.kind === 'used' || state.kind === 'expired') {
    const messageId = state.kind === 'used' ? 'invite.used.message' : 'invite.expired.message'
    return (
      <SplitAuthLayout
        heading={intl.formatMessage({ id: 'invite.used.welcome' })}
        subheading={intl.formatMessage({ id: messageId })}
        dataTestId='invite-page-used'
        back={
          <BackButton to='/login'>{intl.formatMessage({ id: 'invite.backToLogin' })}</BackButton>
        }
      >
        <Button
          variant='primary'
          size='lg'
          block={true}
          type='button'
          onClick={() => {
            window.location.assign(recoveryHref(returnTo))
          }}
        >
          {intl.formatMessage({ id: 'invite.used.action' })}
        </Button>
      </SplitAuthLayout>
    )
  }

  // The invitation is alive but its code was stripped from the address bar (a
  // reload, a session restore). The mail still holds a working link, so the way
  // on is to reopen it — never to reprint the code somewhere it can be read.
  // Nothing to offer on-screen: the only action lives in the invitee's inbox.
  if (state.kind === 'needsCode') {
    return (
      <SplitAuthLayout
        heading={intl.formatMessage({ id: 'invite.needsCode.welcome' })}
        dataTestId='invite-page-needs-link'
        back={
          <BackButton to='/login'>{intl.formatMessage({ id: 'invite.backToLogin' })}</BackButton>
        }
      >
        <InlineMessage variant='info'>
          {intl.formatMessage({ id: 'invite.needsCode.message' })}
        </InlineMessage>
      </SplitAuthLayout>
    )
  }

  return (
    <SplitAuthLayout
      heading={intl.formatMessage({ id: 'invite.welcome' })}
      subheading={intl.formatMessage({ id: 'invite.intro' })}
      dataTestId='invite-page'
      back={<BackButton to='/login'>{intl.formatMessage({ id: 'invite.backToLogin' })}</BackButton>}
    >
      {formError && <InlineMessage variant='error'>{formError}</InlineMessage>}
      <Box
        component='form'
        noValidate={true}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
        onSubmit={handleSubmit}
      >
        <TextField
          name='password'
          type='password'
          icon={<LockIcon />}
          placeholder={intl.formatMessage({ id: 'invite.field.password.placeholder' })}
          label={intl.formatMessage({ id: 'invite.field.password.label' })}
          autoComplete='new-password'
          autoFocus={true}
          required={true}
          error={passwordError}
          onChange={(event) => {
            setPassword(event.currentTarget.value)
            clearPasswordError()
          }}
        />
        <TextField
          name='confirmation'
          type='password'
          icon={<LockIcon />}
          placeholder={intl.formatMessage({ id: 'invite.field.confirm.placeholder' })}
          label={intl.formatMessage({ id: 'invite.field.confirm.label' })}
          autoComplete='new-password'
          required={true}
          error={mismatch ? intl.formatMessage({ id: 'invite.error.mismatch' }) : null}
          onChange={(event) => {
            setConfirmation(event.currentTarget.value)
          }}
        />
        <SubmitRow
          submitLabel={intl.formatMessage({
            id: submitting ? 'invite.submit.pending' : 'invite.submit',
          })}
          submitDisabled={submitting || !bothFilled || mismatch}
        />
      </Box>
    </SplitAuthLayout>
  )
}

// Only `return_to` survives the hop: /recovery has no flow yet, so it bounces
// through Kratos to mint one and every other query parameter is dropped on the
// way. Kratos does carry return_to across that bounce.
const recoveryHref = (returnTo: string): string =>
  `/recovery?return_to=${encodeURIComponent(returnTo)}`

export default InvitePage
